import { useCallback, useEffect, useRef } from 'react';

const DEFAULT_DWELL_MS = 125;

/** Schedules at most one speculative read after deliberate pointer dwell. */
export function useHoverPrefetch(delayMs = DEFAULT_DWELL_MS): {
  schedule: (prefetch: () => void | Promise<void>) => void;
  cancel: () => void;
} {
  const timeoutRef = useRef<number | null>(null);

  const cancel = useCallback(() => {
    if (timeoutRef.current != null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const schedule = useCallback((prefetch: () => void | Promise<void>) => {
    cancel();
    timeoutRef.current = window.setTimeout(() => {
      timeoutRef.current = null;
      Promise.resolve(prefetch()).catch((error: unknown) => {
        // Speculative work never changes the destination's normal error flow.
        console.warn('Could not prefetch hover target', error);
      });
    }, delayMs);
  }, [cancel, delayMs]);

  useEffect(() => cancel, [cancel]);

  return { schedule, cancel };
}
